import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CopyIcon from 'icons/CopyIcon'
import { DiamondsTable } from 'lib/components/diamondsTable'
import { Diamond, BookmarksModalProps, BookmarksModalContentProps } from 'types/interfaces'
import { Roles } from 'constants/roles'
import logger from 'utils/logger'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useAppContext } from 'lib/context/AppContext'
import { ResponsiveModal, ResponsiveModalContent, ResponsiveModalFooterDesktop, ResponsiveModalHeaderDesktop, ResponsiveModalHeaderMobile } from '../ResponsiveModal/ResponsiveModal'
import { ResponsiveModalProps } from '@unbridaled/ui-components/ResponsiveModal'
import { Button } from '@unbridaled/ui-components'

const ModalContent = ({
  shopName,
  config,
  role,
  isMemoMode,
  stoneType,
  link,
  setLink,
  linkWithoutParams,
  stones,
}: BookmarksModalContentProps): React.ReactNode => {
  const { t } = useTranslation()

  const copyToClipboard = async (link: string, event: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link)

      if (event === 'click') {
        const copiedNotice = document.querySelector('.unbridaled-copied')
        if (copiedNotice) {
          copiedNotice.classList.add('show')
          setTimeout(() => {
            copiedNotice.classList.remove('show')
          }, 1400)
        }
      }
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  return (
    <div className="unbridaled-popup-content-wrapper">
      {stoneType !== 'gemstone' || role === Roles.Merchant || isMemoMode ? (
        <>
          <div>
            <h4 className="unbridaled-popup-subtitle">{t('bookmarksModal.link')}</h4>
            <div className="unbridaled-link-preview-wrapper">
              <input className="unbridaled-link-preview" type="text" readOnly value={link} />
              <CopyIcon
                onClick={(): void => {
                  copyToClipboard(link, 'click')
                }}
              />
              <span className="unbridaled-copied">{t('bookmarksModal.copied')}</span>
            </div>
          </div>
          <div>
            <h4 className="unbridaled-popup-subtitle">{t('bookmarksModal.jewelryOptions')}</h4>
            <p>{t('bookmarksModal.jewelryOptionsHint', { stoneType, context: stoneType })}</p>
            <select
              className="unbridaled-jewelry-type"
              name="unbridaledJewelryOptions"
              id="unbridaled-jewelry-type"
              onChange={(e): void => {
                const jType = e.target.value
                const params = jType != 'all' ? `?jewelry_type=${e.target.value}` : ''
                const fullLink = `${linkWithoutParams}${params}`
                setLink(fullLink)
                copyToClipboard(fullLink, 'change')
              }}
            >
              <option value="all">{t('bookmarksModal.allJewelry')}</option>
              <option value="ring">{t('bookmarksModal.rings')}</option>
              <option value="pendant">{t('bookmarksModal.pendants')}</option>
              <option value="bracelet">{t('bookmarksModal.bracelets')}</option>
            </select>
          </div>
        </>
      ) : null}
      <div>
        <h4 className="unbridaled-popup-subtitle">{t('bookmarksModal.linkPreview')}</h4>
        <div className="unbridaled-diamond-data">
          <div className="unbridaled-header-row">
            <span>
              <b>{shopName}</b> {t('bookmarksModal.curatedIntroSuffix', { stoneType, context: stoneType })}
            </span>
          </div>
          {stones.length ? (
            <DiamondsTable selectedDiamonds={stones} sourceTypes={config.sourceTypes} />
          ) : (
            <>
              <Skeleton height={20} containerClassName="diamond-skeleton-title" />
              <Skeleton height={80} containerClassName="diamond-skeleton-title" />
              <Skeleton height={80} containerClassName="diamond-skeleton-title" />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export type ModalProps = BookmarksModalProps & ResponsiveModalProps

export const BookmarksModal = ({
  open,
  shopName,
  favorites,
  onClose,
  config,
  role,
  isMemoMode,
  stoneType,
  title,
  ...props
}: ModalProps): React.ReactNode => {
  const { t } = useTranslation()
  const modalTitle = title ?? t('bookmarksModal.title')
  const [link, setLink] = useState('')
  const [stones, setStones] = useState([] as Diamond[])

  const { diamondStore, currencyInfo } = useAppContext()
  const skus = favorites.map(item => item.sku)

  const fetchStones = async (favorites: Diamond[]): Promise<void | null> => {
    try {
      const skus = favorites.map(item => item.sku)
      const body: { [key: string]: any } = {
        filters: { sku: skus }
      }
      if (role !== Roles.Merchant && !isMemoMode) {
        body.currency_code = currencyInfo.currency
      } else {
        body.currency_code = undefined
      }
      const data = await diamondStore.getDiamond(body, stoneType, role)
      setStones(data?.gems || [])
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  useEffect(() => {
    if (open) {
      fetchStones(favorites)
    }
  }, [open, favorites])

  const handleClose = (): void => {
    onClose()
  }

  const linkWithoutParams =
    window.location.origin + `/apps/ub/curated_${stoneType}s/` + btoa(skus.join(','))

  useEffect(() => {
    setLink(linkWithoutParams)
  }, [linkWithoutParams])

  const mailBody = encodeURIComponent(
    t('bookmarksModal.mailtoBody', { shopName, stoneType, context: stoneType }) + link
  )

  const mailSubject = encodeURIComponent(t('bookmarksModal.mailtoSubject', { stoneType, context: stoneType }))
  const mailLink = `mailto:?subject=${mailSubject}&body=${mailBody}`

  return (
    <ResponsiveModal size="uui-w-[70%] uui-max-w-2xl" {...props} onClose={handleClose} open={open}>
      <ResponsiveModalHeaderDesktop title={modalTitle} onClose={handleClose} />
      <ResponsiveModalHeaderMobile
        title={modalTitle}
        leadingIcon={
          <Button
            className="uui-flex-1 mobile-modal-button uui-justify-start unbridaled-cancel"
            onClick={handleClose}
          >
            {t('common.cancel')}
          </Button>
        }
        trailingIcon={
          <a href={mailLink} className="uui-justify-end" id="email">
            {t('bookmarksModal.composeEmail')}
          </a>
        }
      />
      <ResponsiveModalContent>
        <ModalContent
          shopName={shopName}
          favorites={favorites}
          config={config}
          role={role}
          isMemoMode={isMemoMode}
          stoneType={stoneType}
          link={link}
          setLink={setLink}
          linkWithoutParams={linkWithoutParams}
          stones={stones}
        />
      </ResponsiveModalContent>
      <ResponsiveModalFooterDesktop>
        <div className="unbridaled-base-popup-options">
          <p className="unbridaled-copied-text">{t('bookmarksModal.linkCopiedAlready')}</p>
          <a href={mailLink} id="email">
            {t('bookmarksModal.composeEmail')}
          </a>
        </div>
      </ResponsiveModalFooterDesktop>
    </ResponsiveModal>
  )
}

